import {
  AutomationActionIdentifier,
  AutomationSource,
  AutomationTriggerIdentifier,
} from 'api/automation-builder/types';
import { NativeFieldFilter } from 'api/dashboard/widget';
import { User } from 'api/models';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { AutomationOperations } from 'components/organisms/tasks/filters/operations';

export enum AutomationType {
  Crm = 'CRM',
  KeyEvents = 'KEY_EVENTS',
  CustomAutomation = 'CUSTOM_AUTOMATION',
}

export enum AutomationTriggerInterpretation {
  EachFieldChangedAndMatching = 'EACH_FIELD_CHANGED_AND_MATCHING',
  AnyFieldChangedAndAllMatching = 'ANY_FIELD_CHANGED_AND_ALL_MATCHING',
}

export enum AutomationObjectType {
  Task = 'TASK',
  Project = 'PROJECT',
  Phase = 'PHASE',
  Form = 'FORM',
  Invoice = 'INVOICE',
  Expense = 'EXPENSE',
  None = 'NONE',
}

export enum AutomationTriggerType {
  ScheduledTrigger = 'SCHEDULED_TRIGGER',
  EventTrigger = 'EVENT_TRIGGER',
  ExternalEventTrigger = 'EXTERNAL_EVENT_TRIGGER',
  DateTrigger = 'DATE_TRIGGER',
  ManualTrigger = 'MANUAL_TRIGGER',
}

export interface SimpleSearchFilter {
  nativeFields: NativeFieldFilter[];
  match: QueryCombinerType;
}

export interface AutomationTriggerV2 {
  uuid: string;
  template: AutomationTriggerIdentifier;
  input: Record<string, unknown>;
  triggerType?: AutomationTriggerType;
  triggerObjectType?: AutomationObjectType;
}

export interface AutomationActionsV2 {
  uuid: string;
  template: AutomationActionIdentifier;
  input: Record<string, unknown>;
}

export interface AutomationPlaceholder {
  id: string;
  variable: string;
}

export interface AutomationTags {
  tag: string;
  fieldId: number;
  referenceId: number;
  referenceType: string;
  keyEventAutomationType: string;
  /**
   * this is just used in FE as of now and even though it comes from BE
   */
  taskTemplateInstanceReferenceId?: number;
  phaseTemplateInstanceReferenceId?: number;
}

export interface AutomationTriggerFilter {
  operation: AutomationOperations;
  value: unknown;
  name: string;
  args?: unknown;
}

export interface TriggerConditionFieldsFilter {
  automationTriggerFields: AutomationTriggerFilter[];
  match?: QueryCombinerType;
}

/** Supports nested filter groups (like advanced-drawer). Each group has match + fields + optional nestedFilter. */
export interface MatchingConditionFieldsFilter {
  automationMatchingConditionFields: NativeFieldFilter[];
  match: QueryCombinerType;
  nestedFilter?: MatchingConditionFieldsFilter[];
}

export interface SelectorConditionFieldsFilter {
  automationSelectorConditionFields: NativeFieldFilter[];
  match: QueryCombinerType;
}

export interface AutomationCondition {
  uuid: string;
  matchingCondition: MatchingConditionFieldsFilter;
  triggerCondition: TriggerConditionFieldsFilter;
  selectorCondition: SelectorConditionFieldsFilter;
}

export interface ActionBlock {
  uuid: string;
  condition: AutomationCondition;
  actions: AutomationActionsV2[];
}

export interface Automation {
  automationId: number;
  automationName: string;
  description?: string;
  order: number;
  createdBy: User;
  automationType: AutomationType;
  triggerInterpretation: AutomationTriggerInterpretation;
  matchingCondition: MatchingConditionFieldsFilter;
  triggerCondition: TriggerConditionFieldsFilter;
  triggerV2: AutomationTriggerV2;
  actionsV2: AutomationActionsV2[];
  placeholders: AutomationPlaceholder[];
  source: AutomationSource;
  sourceId?: number;
  createdAt: string;
  updatedAt: string;
  accountId: number;
  deleted: boolean;
  enabled: boolean;
  tags: AutomationTags;
  actionBlocks?: ActionBlock[];
  mirrorFromTemplateId?: number;
  createdFromTemplateId?: number;
}

export interface AutomationRunOrder {
  automationRunOrderId: number;
  source: AutomationSource;
  sourceOrder: number[];
  exitOnFirstMatch: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: number;
  updatedBy: number;
}

export const TASK_TRIGGERS = [
  AutomationTriggerIdentifier.TaskCreatedV2,
  AutomationTriggerIdentifier.TaskDeleted,
  AutomationTriggerIdentifier.TaskDueDateIsUpdated,
  AutomationTriggerIdentifier.TaskStatusIsUpdatedV2,
  AutomationTriggerIdentifier.TaskAnyFieldIsUpdatedV2,
  AutomationTriggerIdentifier.TaskApprovalStatusIsUpdated,
  AutomationTriggerIdentifier.TaskApprovalRequest,
  AutomationTriggerIdentifier.TaskCSATSubmitted,
  AutomationTriggerIdentifier.NewMessageInTask,
  AutomationTriggerIdentifier.TaskBecomesOverdue,
  AutomationTriggerIdentifier.TaskRemainsInCertainStatus,
  AutomationTriggerIdentifier.TaskAssigneeIsUpdated,
  AutomationTriggerIdentifier.AnyTaskDueDateOverdue,
  AutomationTriggerIdentifier.TaskSpotlighted,
  AutomationTriggerIdentifier.TaskProjectFinancialsBudgetsUpdated,
];

export const PHASE_TRIGGERS = [
  AutomationTriggerIdentifier.PhaseCreatedV2,
  AutomationTriggerIdentifier.PhaseStartDateIsChangedV2,
  AutomationTriggerIdentifier.PhaseDueDateIsChangedV2,
  AutomationTriggerIdentifier.PhaseStatusIsChangedV2,
  AutomationTriggerIdentifier.PhaseNameIsChangedV2,
  AutomationTriggerIdentifier.PhaseDeleted,
  AutomationTriggerIdentifier.PhaseProjectFinancialsBudgetsUpdated,
];

export const PROJECT_TRIGGERS = [
  AutomationTriggerIdentifier.ProjectCreated,
  AutomationTriggerIdentifier.ProjectAnyFieldIsUpdated,
  AutomationTriggerIdentifier.ProjectFinancialFieldIsUpdated,
  AutomationTriggerIdentifier.ProjectArchived,
  AutomationTriggerIdentifier.ProjectDeleted,
  AutomationTriggerIdentifier.CustomerInvitedToProject,
  AutomationTriggerIdentifier.TeamMemberInvitedToProject,
  AutomationTriggerIdentifier.ProjectPlaceholderResolved,
  AutomationTriggerIdentifier.ProjectCustomerPortalChanged,
];
export const COMPANY_TRIGGERS = [
  AutomationTriggerIdentifier.CompanyCreated,
  AutomationTriggerIdentifier.CompanyAnyFieldIsUpdated,
  AutomationTriggerIdentifier.CompanyDeleted,
];

export const TASK_ACTIONS = [
  AutomationActionIdentifier.CreateTaskMessage,
  AutomationActionIdentifier.CreateTask,
  AutomationActionIdentifier.CreateSubTask,
  AutomationActionIdentifier.UpdateTaskAssignee,
  AutomationActionIdentifier.UpdateTaskStartDate,
  AutomationActionIdentifier.UpdateTaskDueDate,
  AutomationActionIdentifier.ShiftTaskDates,
  AutomationActionIdentifier.UpdateTaskStatus,
  AutomationActionIdentifier.UpdateTaskPhase,
  AutomationActionIdentifier.UpdateTaskField,
  AutomationActionIdentifier.RequestTaskApproval,
  AutomationActionIdentifier.SpotlightTask,
  AutomationActionIdentifier.DeleteTask,
  AutomationActionIdentifier.FollowTask,
];

export const PHASE_ACTIONS: AutomationActionIdentifier[] = [
  AutomationActionIdentifier.UpdatePhaseStartDate,
  AutomationActionIdentifier.UpdatePhaseDueDate,
  AutomationActionIdentifier.UpdatePhaseStatus,
  AutomationActionIdentifier.UpdatePhaseName,
  AutomationActionIdentifier.DeletePhase,
];

export const PROJECT_ACTIONS: AutomationActionIdentifier[] = [
  AutomationActionIdentifier.ImportTemplate,
  AutomationActionIdentifier.UpdateProjectFields,
  AutomationActionIdentifier.UpdateProjectFinancialFields,
  AutomationActionIdentifier.CreateDocument,
  AutomationActionIdentifier.SendProjectUpdates,
  AutomationActionIdentifier.CreateProjectMessage,
  AutomationActionIdentifier.UpdateResourceAllocation,
  AutomationActionIdentifier.DeleteAllocations,
  AutomationActionIdentifier.ShiftAllocations,
  AutomationActionIdentifier.UpdateProjectStatus,
  AutomationActionIdentifier.ChangeCustomerPortalForProject,
  AutomationActionIdentifier.ArchiveProject,
  AutomationActionIdentifier.DeleteProject,
  AutomationActionIdentifier.ShiftProjectDates,
];

export const GLOBAL_SIGNAL_TRIGGERS: AutomationTriggerIdentifier[] = [
  AutomationTriggerIdentifier.SignalOccurrenceCreated,
];
export const RESOURCE_REQUEST_ACTIONS: AutomationActionIdentifier[] = [
  AutomationActionIdentifier.AssignResourceRequestApprover,
];
export const COMPANY_ACTIONS: AutomationActionIdentifier[] = [
  AutomationActionIdentifier.UpdateCompanyFields,
];

export const GLOBAL_SIGNAL_GROUPED_ACTIONS: AutomationActionIdentifier[] = [
  ...COMPANY_ACTIONS,
];

export const GLOBAL_SIGNAL_BUILDER_ACTIONS: AutomationActionIdentifier[] = [
  AutomationActionIdentifier.UpdateCompanyFields,
  AutomationActionIdentifier.NotifySomeone,
  AutomationActionIdentifier.SendEmail,
  AutomationActionIdentifier.PushSignalWebHook,
  AutomationActionIdentifier.ExitAutomation,
];

export const FORM_TRIGGERS: AutomationTriggerIdentifier[] = [
  AutomationTriggerIdentifier.FormDueDateIsUpdated,
  AutomationTriggerIdentifier.FormStatusIsUpdated,
  AutomationTriggerIdentifier.FormAssigneeIsUpdated,
  AutomationTriggerIdentifier.FormAnyFieldIsUpdated,
  AutomationTriggerIdentifier.NewMessageInForm,
  AutomationTriggerIdentifier.FormResponseSubmitted,
  AutomationTriggerIdentifier.FormCreated,
  AutomationTriggerIdentifier.FormApprovalStatusIsUpdated,
  AutomationTriggerIdentifier.FormApprovalRequest,
  AutomationTriggerIdentifier.FormCSATSubmitted,
  AutomationTriggerIdentifier.FormSpotlighted,
  AutomationTriggerIdentifier.FormDeleted,
];

export const RESOURCE_REQUEST_TRIGGERS: AutomationTriggerIdentifier[] = [
  AutomationTriggerIdentifier.ResourceRequestCreated,
  AutomationTriggerIdentifier.ResourceRequestStatusUpdated,
];

export const BUDGET_TRIGGERS: AutomationTriggerIdentifier[] = [
  AutomationTriggerIdentifier.BudgetCreated,
  AutomationTriggerIdentifier.BudgetFieldUpdated,
  AutomationTriggerIdentifier.BudgetDeleted,
  AutomationTriggerIdentifier.PhaseProjectFinancialsBudgetsUpdated,
  AutomationTriggerIdentifier.TaskProjectFinancialsBudgetsUpdated,
];

export const BUDGET_ACTIONS: AutomationActionIdentifier[] = [
  AutomationActionIdentifier.CreateBudget,
  AutomationActionIdentifier.UpdateBudgetField,
];

export const INVOICE_TRIGGERS: AutomationTriggerIdentifier[] = [
  AutomationTriggerIdentifier.InvoiceMovesFromDraftToInReview,
];

export const INVOICE_ACTIONS: AutomationActionIdentifier[] = [
  AutomationActionIdentifier.AssignApprovalToInvoice,
];

export const SHEET_LEVEL_TRIGGERS = [
  AutomationTriggerIdentifier.SheetCreated,
  AutomationTriggerIdentifier.SheetDeleted,
  AutomationTriggerIdentifier.SheetAssigneeUpdated,
  AutomationTriggerIdentifier.SheetDueDateUpdated,
  AutomationTriggerIdentifier.SheetStatusUpdated,
  AutomationTriggerIdentifier.SheetFieldUpdated,
  AutomationTriggerIdentifier.SheetApprovalResponseReceived,
  AutomationTriggerIdentifier.NewMessageInSheet,
  AutomationTriggerIdentifier.SheetBecomesOverdue,
];
export const SHEET_TAB_TRIGGERS = [
  AutomationTriggerIdentifier.RowMatchesCondition,
  AutomationTriggerIdentifier.RowAdded,
  AutomationTriggerIdentifier.RowUpdated,
  AutomationTriggerIdentifier.RowDeleted,
  AutomationTriggerIdentifier.SheetTabCreated,
  AutomationTriggerIdentifier.SheetTabDeleted,
  AutomationTriggerIdentifier.SheetTabStatusUpdated,
  AutomationTriggerIdentifier.SheetTabAssigneeUpdated,
  AutomationTriggerIdentifier.SheetTabBecomesOverdue,
];

export const SHEET_MANUAL_TRIGGERS = [
  AutomationTriggerIdentifier.SheetManualTrigger,
];

export const SHEET_ACTIONS: AutomationActionIdentifier[] = [
  AutomationActionIdentifier.CreateSheetTab,
  AutomationActionIdentifier.UpdateSheetAssignee,
  AutomationActionIdentifier.UpdateSheetStartDate,
  AutomationActionIdentifier.UpdateSheetDueDate,
  AutomationActionIdentifier.ShiftSheetDates,
  AutomationActionIdentifier.UpdateSheetStatus,
  AutomationActionIdentifier.UpdateSheetField,
  AutomationActionIdentifier.FreezeSheet,
  AutomationActionIdentifier.RequestSheetApproval,
  AutomationActionIdentifier.FollowSheet,
  AutomationActionIdentifier.DeleteSheet,
  AutomationActionIdentifier.CreateSheetMessage,
];
export const SHEET_TAB_ACTIONS: AutomationActionIdentifier[] = [
  AutomationActionIdentifier.AddRow,
  AutomationActionIdentifier.UpdateRow,
  AutomationActionIdentifier.DeleteRow,
  AutomationActionIdentifier.UpdateSheetTabAssignee,
  AutomationActionIdentifier.UpdateSheetTabDueDate,
  AutomationActionIdentifier.FreezeSheetTab,
  AutomationActionIdentifier.RequestSheetTabApproval,
  AutomationActionIdentifier.DeleteSheetTab,
];

export const SHEET_ACTIONS_ORDER = SHEET_ACTIONS;
export const SHEET_TAB_ACTIONS_ORDER = SHEET_TAB_ACTIONS;
export const EXPENSE_TRIGGERS: AutomationTriggerIdentifier[] = [
  AutomationTriggerIdentifier.ExpenseMovedFromDraftToSubmitted,
];

export const EXPENSE_ACTIONS: AutomationActionIdentifier[] = [
  AutomationActionIdentifier.AutoApproveExpense,
  AutomationActionIdentifier.AssignApprovalToExpense,
];
